/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-expressions */
import { Container } from '@mui/material'
import Layout from 'components/Layout/Layout'
import MenuTab from 'components/MenuTab/Index'
import {
  paymentEnableData,
  restuarantData,
  isPaymentStatusLoading,
} from 'features/restaurant/restaurant'
import {
  setTabDetail,
  setTabItems,
  setTabSeats,
  tabDetail,
  tabItems,
  tabIsLoading,
} from 'features/tab/tab'
import { IRestaurantDetailData } from 'interfaces/restaurant'
import { useSelector, useDispatch } from 'react-redux'
import EmptyTabView from 'components/EmptyTabView/Index'
import YourTabFooter from 'components/YourTabFooter/YourTabFooter'
import PayNowFooter from 'components/Footer/PayNowFooter'
import TableDetails from 'components/TableDetails/Index'
import { useMemo, useEffect, useCallback, useRef, useState } from 'react'
import YourTabSkeleton from 'components/YourTabSkeleton/Index'
import tabService from 'services/tab.service'
import { ITabItem } from 'interfaces/tab'
import { itemsToSeat, isOrderingEnabledAndHealthy } from 'utility/helper'
import { menuList } from 'features/menu/menu'

function YourTab() {
  const dispatch = useDispatch()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const restaurantData: IRestaurantDetailData = useSelector(restuarantData)
  const menus = useSelector(menuList)
  const tabDetailData = useSelector(tabDetail)
  const items = useSelector(tabItems)
  const isPaymentEnable = useSelector(paymentEnableData)
  const isTabLoading = useSelector(tabIsLoading)
  const paymentStatusLoading = useSelector(isPaymentStatusLoading)

  // Note: Menu availability and ordering logic now handled in EmptyTabView component

  // Track tabId changes from sessionStorage (including SignalR updates)
  const [currentTabId, setCurrentTabId] = useState(() =>
    sessionStorage.getItem('tabId')
  )

  // Background data refresh function
  const refreshTabDataInBackground = useCallback(async () => {
    try {
      const tableId = sessionStorage.getItem('posTableId')
      const establishmentId = sessionStorage.getItem('establishmentId')

      if (!tableId || !establishmentId) {
        console.warn(
          'Missing tableId or establishmentId for background refresh',
          { tableId, establishmentId }
        )
        return
      }

      console.log('Starting background tab data refresh...', {
        tableId: Number(tableId),
        establishmentId: Number(establishmentId),
      })

      const tabRes = await tabService.getTabs({
        posTableId: Number(tableId),
        locationId: Number(establishmentId),
      })

      console.log('Background refresh API response received:', {
        success: !!tabRes?.data?.data,
        ordersCount: tabRes?.data?.data?.orders?.length || 0,
        hasTabPayments: !!tabRes?.data?.data?.tabPayments,
      })

      if (!tabRes?.data?.data) {
        console.warn('Background refresh: No data received from API')
        return
      }

      const response = tabRes.data.data

      // Update session storage with new tab ID if it changed
      if (response.tabId) {
        const sessionTabId = sessionStorage.getItem('tabId')
        if (sessionTabId !== response.tabId.toString()) {
          console.log('Tab ID changed, updating session storage:', {
            oldTabId: sessionTabId,
            newTabId: response.tabId,
          })
          sessionStorage.setItem('tabId', response.tabId.toString())
          setCurrentTabId(response.tabId.toString()) // Update state to trigger re-render
        }
      }

      // Process orders before updating Redux
      if (response.orders) {
        response.orders.forEach((ele) => {
          ele.orderDate = null
          // Selection state preservation is now handled at the Redux level
        })
      }

      console.log('Updating Redux store with fresh background data...', {
        ordersCount: response.orders?.length || 0,
        tabId: response.tabId,
        publicTabId: response.publicTabId,
      })

      // Silently update Redux store with fresh data
      dispatch(setTabDetail(response))

      // Process tab items and seats
      if (response.orders && response.orders.length > 0) {
        let allItems: ITabItem[] = []
        response.orders.forEach((element) => {
          allItems = [...allItems, ...element.items]
        })
        dispatch(setTabSeats(itemsToSeat(allItems)))
        dispatch(setTabItems(allItems))

        console.log('Background refresh: Updated items and seats', {
          totalItems: allItems.length,
          seatsCount: itemsToSeat(allItems).length,
        })
      } else {
        dispatch(setTabItems([]))
        console.log('Background refresh: No orders found, cleared items')
      }

      console.log('Background tab data refresh completed successfully')
    } catch (error) {
      console.error('Failed to refresh tab data in background:', error)
      // Silently fail - don't disrupt user experience
      // But we should still log the full error for debugging
      if (error instanceof Error) {
        console.error('Background refresh error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
        })
      }
    }
  }, [dispatch])

  // Watch for sessionStorage changes (including SignalR updates)
  useEffect(() => {
    const checkTabIdChanges = () => {
      const sessionTabId = sessionStorage.getItem('tabId')
      if (sessionTabId !== currentTabId) {
        console.log(
          'Detected tabId change from external source (likely SignalR):',
          {
            oldTabId: currentTabId,
            newTabId: sessionTabId,
          }
        )
        setCurrentTabId(sessionTabId)

        // If tabId changed to a real tab ID (not 'closed'), refresh tab data immediately
        if (sessionTabId && sessionTabId !== 'closed') {
          console.log(
            'TabId changed to active tab, refreshing data immediately...'
          )
          refreshTabDataInBackground()
        }
      }
    }

    // Check for changes every 2 seconds (reduced from 500ms for better performance)
    const intervalId = setInterval(checkTabIdChanges, 2000)

    return () => clearInterval(intervalId)
  }, [currentTabId, refreshTabDataInBackground])

  // Load tab data when component mounts
  useEffect(() => {
    // Always load data on mount, regardless of existing data
    // This ensures we get fresh data when navigating to YourTab
    refreshTabDataInBackground()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array - only run on mount

  // Set up periodic refresh every minute
  useEffect(() => {
    // Only start interval if we have tab data, payment status is loaded, and payment is enabled
    if (
      tabDetailData &&
      !isTabLoading &&
      !paymentStatusLoading &&
      isPaymentEnable?.status
    ) {
      console.log('Starting periodic tab data refresh (every 60 seconds)')

      intervalRef.current = setInterval(() => {
        refreshTabDataInBackground()
      }, 60000) // 60 seconds = 60,000 milliseconds
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        console.log('Clearing periodic tab data refresh interval')
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [
    tabDetailData,
    isTabLoading,
    paymentStatusLoading,
    isPaymentEnable?.status,
    refreshTabDataInBackground,
  ])

  // Cleanup interval when component unmounts
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const showPaymentDisabledMessage =
    !isPaymentEnable?.status ||
    isPaymentEnable?.isTableDisable ||
    !isPaymentEnable.isHealthy ||
    tabDetailData?.tabPayments?.isPosPaid ||
    tabDetailData?.isCheckChanged

  // Simplified calculations using pre-calculated order values
  const { emptyOrder, selectedSum, isItemSelected, selectedDiscounts } =
    useMemo(() => {
      // If loading (either tab or payment status), return default values
      if (isTabLoading || paymentStatusLoading || !tabDetailData) {
        return {
          emptyOrder: true,
          selectedSum: 0,
          isItemSelected: false,
          selectedDiscounts: 0,
        }
      }

      // Check if there are no orders
      const hasNoOrders =
        items.length === 0 || tabDetailData?.orders?.length === 0

      // Check if there are any open orders (isOpen = true)
      // Orders should be displayed as long as they are open, regardless of payment status
      const hasOpenOrders =
        tabDetailData?.orders?.some((order) => order.isOpen === true) ?? false

      const emptyOrderResult = hasNoOrders || !hasOpenOrders

      // Use pre-calculated order totals directly - no complex calculations needed!
      let selectedSumResult = 0
      let selectedDiscountsResult = 0

      tabDetailData?.orders?.forEach((order) => {
        if (order.isSelected && order.isOpen) {
          // Simply use the order's pre-calculated subtotal and discounts
          selectedSumResult += order.subTotal
          selectedDiscountsResult += order.totalDiscounts
        }
      })

      // Check if any unpaid item is selected
      const isItemSelectedResult = items.some(
        (item) => item.isSelected === true && !item.isPaid
      )

      return {
        emptyOrder: emptyOrderResult,
        selectedSum: selectedSumResult,
        isItemSelected: isItemSelectedResult,
        selectedDiscounts: selectedDiscountsResult,
      }
    }, [tabDetailData, items, isTabLoading, paymentStatusLoading])

  const calculateSelectedTax = useMemo(() => {
    return () => {
      if (!tabDetailData?.orders || isTabLoading || paymentStatusLoading)
        return 0
      // Simply sum up pre-calculated tax from selected orders
      return tabDetailData.orders
        .filter((order) => order.isSelected && order.isOpen)
        .reduce((acc, order) => acc + order.tax, 0)
    }
  }, [tabDetailData?.orders, isTabLoading, paymentStatusLoading])

  const calculateSelectedServiceCharge = useMemo(() => {
    return () => {
      if (!tabDetailData?.orders || isTabLoading || paymentStatusLoading)
        return 0
      // Simply sum up pre-calculated service charges from selected orders
      return tabDetailData.orders
        .filter((order) => order.isSelected && order.isOpen)
        .reduce((acc, order) => acc + order.totalServiceCharges, 0)
    }
  }, [tabDetailData?.orders, isTabLoading, paymentStatusLoading])

  const calculateTotalAfterTax = useMemo(() => {
    return () => {
      if (!tabDetailData?.orders || isTabLoading || paymentStatusLoading)
        return '0.00'
      // Simply sum up pre-calculated totals from selected orders (total already includes everything)
      const totalAfterTax = tabDetailData.orders
        .filter((order) => order.isSelected && order.isOpen)
        .reduce((acc, order) => acc + order.total, 0)

      return totalAfterTax.toFixed(2)
    }
  }, [tabDetailData?.orders, isTabLoading, paymentStatusLoading])

  const getStatusMessage = () => {
    // Check if table is closed using tracked state - THIS TAKES PRIORITY
    const isTableClosed = currentTabId === 'closed'

    // If table is closed, show completion message instead of "No Tab Yet"
    if (isTableClosed) {
      return 'All Set!'
    }

    // Check for table unavailable conditions only if table is not closed
    if (!isPaymentEnable?.status) {
      return 'Payment is currently disabled for this table'
    }

    if (isPaymentEnable?.isTableDisable) {
      return 'This table is currently out of service'
    }

    if (!isPaymentEnable?.isHealthy) {
      return 'Payment system is temporarily unavailable'
    }

    // Show "No Tab Yet" for empty orders
    if (emptyOrder) {
      return 'No Tab Yet'
    }

    // Only show server message when table is active but payment is disabled due to POS issues
    if (
      showPaymentDisabledMessage &&
      (tabDetailData?.tabPayments?.isPosPaid || tabDetailData?.isCheckChanged)
    ) {
      return 'Please see your server at this time to pay for your tab.'
    }

    return 'No Tab Yet'
  }

  const getStatusSubMessage = () => {
    // Check if table is closed using tracked state - THIS TAKES PRIORITY
    const isTableClosed = currentTabId === 'closed'

    // If table is closed, always show thank you message regardless of health status
    if (isTableClosed) {
      return `Thank you for dining with us! - ${restaurantData.restaurantName}`
    }

    // Check for table unavailable conditions only if table is not closed
    if (
      !isPaymentEnable?.status ||
      isPaymentEnable?.isTableDisable ||
      !isPaymentEnable?.isHealthy
    ) {
      return `Please see your server at this time. The table is temporarily unavailable. - ${restaurantData.restaurantName}`
    }

    // Only show server thank you message when payment was completed via POS
    if (
      showPaymentDisabledMessage &&
      (tabDetailData?.tabPayments?.isPosPaid || tabDetailData?.isCheckChanged)
    ) {
      return `Thank you! - ${restaurantData.restaurantName}`
    }

    // Check if ordering is enabled and table health is good
    const isOrderingEnabled = isOrderingEnabledAndHealthy(
      menus,
      isPaymentEnable
    )

    // Default message for empty orders or normal states
    if (emptyOrder && isOrderingEnabled) {
      // Show ordering enabled message when tab is empty and ordering is available AND healthy
      return (
        <>
          Order now with FasTab
          <br />
          <br />
          Just placed an order?
          <br />
          You&apos;ll see your items shortly
        </>
      )
    }

    // Default message for empty orders when ordering is not enabled
    return (
      <>
        Order with your server and pay with FasTab
        <br />
        <br />
        Just placed an order?
        <br />
        You&apos;ll see your items shortly
      </>
    )
  }

  // Check if table is available for orders and payment
  const isTableAvailable = () => {
    const isTableClosed = currentTabId === 'closed'
    if (isTableClosed) return true // Closed tables can still show content

    return (
      isPaymentEnable?.status &&
      !isPaymentEnable?.isTableDisable &&
      isPaymentEnable?.isHealthy
    )
  }

  const renderContent = () => {
    // Show skeleton loading while payment status is loading OR tab is loading
    if (paymentStatusLoading || isTabLoading) {
      return <YourTabSkeleton />
    }

    // Once payment status is loaded, check if table is available
    const tableAvailable = isTableAvailable()

    // If table is not available (disabled, unhealthy, out of service), skip orders entirely
    if (!tableAvailable) {
      return (
        <EmptyTabView
          restaurantData={restaurantData}
          showPaymentDisabledMessage
          getStatusMessage={getStatusMessage}
          getStatusSubMessage={getStatusSubMessage}
          emptyOrder={emptyOrder}
        />
      )
    }

    // Table is available - show orders if present, otherwise show empty view
    if (!emptyOrder) {
      return (
        <>
          <TableDetails
            tabDetailData={tabDetailData}
            items={items}
            selectedSum={selectedSum}
            selectedDiscounts={selectedDiscounts}
            calculateSelectedTax={calculateSelectedTax}
            calculateSelectedServiceCharge={calculateSelectedServiceCharge}
            calculateTotalAfterTax={calculateTotalAfterTax}
          />
          {!showPaymentDisabledMessage ? (
            <PayNowFooter
              remainingAmount={
                tabDetailData?.tabPayments !== null
                  ? tabDetailData?.tabPayments?.remaining
                  : null
              }
              isItemSelected={isItemSelected}
            />
          ) : null}
        </>
      )
    }

    // Show empty view when there are no orders or tab is empty
    return (
      <EmptyTabView
        restaurantData={restaurantData}
        showPaymentDisabledMessage={showPaymentDisabledMessage}
        getStatusMessage={getStatusMessage}
        getStatusSubMessage={getStatusSubMessage}
        emptyOrder={emptyOrder}
      />
    )
  }

  return (
    <>
      <Layout title="Your Table | FasTab">
        <section className="wrapper with-footer">
          <Container maxWidth="md">
            <MenuTab yourTab />
            {renderContent()}
          </Container>
          {/* Only show footer when not loading, skeleton handles its own footer */}
          {!paymentStatusLoading &&
            !isTabLoading &&
            isPaymentEnable?.isHealthy && (
              <YourTabFooter
                emptyOrder={emptyOrder}
                isTableDisable={isPaymentEnable.isTableDisable}
                remainingAmount={
                  tabDetailData?.tabPayments !== null
                    ? tabDetailData?.tabPayments?.remaining
                    : null
                }
                isItemSelected={isItemSelected}
              />
            )}
        </section>
      </Layout>
    </>
  )
}

export default YourTab
